import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type MouseEvent } from 'react'
import {
  installAllSoftseeOsPatches,
  installSoftseeOsPatches,
  osUpdatesPageQuery,
  osUpdatesTableQuery,
  setOsAutoUpdate,
  type OsPatchRow,
  type OsTile,
} from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Operating System - opteemum/devices/details/osUpdates.php: cele
 * patru casute, grila os-facts, To install / Recently installed, Auto
 * Update.
 *
 * "Install All Updates" posteaza la `/devices/{id}/os-updates/install-all`
 * (Opteemum::installOsUpdates, dreptul `opteemum.install_os_updates.read`),
 * Install pe rand la `/os-updates/install`, iar bifa Auto Update la
 * `/os-updates/auto-update`. Aceleasi texte ca osUpdates.php / osUpdates-1.js.
 * Din tichet, comenzile poarta numarul lui.
 */

const MISSING = 'deviceOsMissing'
const INSTALLED = 'deviceOsInstalled'

const WINDOWS_HEADS = [
  'Name',
  'Severity',
  'Downloaded',
  'Size',
  'Require source',
  'Mandatory',
  'Beta',
  'Reboot behaviour',
  'Action status',
]
const LINUX_HEADS = ['Package', 'Available version', 'Current version', 'Update', 'Action status']

export function DeviceSoftseeOs({ id, ticket }: ScreenProps) {
  const page = useQuery(osUpdatesPageQuery(id))
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [missingSearch, setMissingSearch] = useState('')
  const [missingStart, setMissingStart] = useState(0)
  const [missingLength, setMissingLength] = useState(10)
  const [installedSearch, setInstalledSearch] = useState('')
  const [installedStart, setInstalledStart] = useState(0)
  const [installedLength, setInstalledLength] = useState(10)

  const missing = useQuery({
    ...osUpdatesTableQuery(id, {
      type: 0,
      start: missingStart,
      length: missingLength,
      search: missingSearch,
    }),
    placeholderData: keepPreviousData,
    enabled: !!page.data && (page.data.open_count ?? page.data.missing_count ?? 0) > 0,
  })
  const installed = useQuery({
    ...osUpdatesTableQuery(id, {
      type: 1,
      start: installedStart,
      length: installedLength,
      search: installedSearch,
    }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
  })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const missingCount = page.data.missing_count
  const openCount = page.data.open_count ?? missingCount
  const linux = page.data.is_linux

  const installAll = async () => {
    if (busy || !window.confirm('Are you sure?')) return
    setBusy(true)
    try {
      const result = await installAllSoftseeOsPatches(id, ticket ?? 0, missingSearch)
      if (!result.ok) {
        toast(result.message ?? 'Something went wrong.', 'Error', 'error')
        return
      }
      toast('Install os updates instruction has been sent.', 'Success')
      await queryClient.invalidateQueries({ queryKey: ['device', id, 'os-updates'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const installOne = async (patch: string) => {
    if (busy || patch === '' || !window.confirm('Are you sure?\n\nThis update will be installed')) return
    setBusy(true)
    try {
      const result = await installSoftseeOsPatches(id, [patch], ticket ?? 0)
      if (!result.ok) {
        toast(result.message ?? 'Install command could not be sent!', 'Error', 'error')
        return
      }
      toast('Install os updates instruction has been sent.', 'Success')
      await queryClient.invalidateQueries({ queryKey: ['device', id, 'os-updates'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onMissingClick = (ev: MouseEvent<HTMLDivElement>) => {
    const one = (ev.target as HTMLElement).closest<HTMLElement>('.install-one-os-update')
    if (!one) return
    ev.preventDefault()
    void installOne(one.dataset.patch ?? '')
  }

  const toggleAutoUpdate = async (on: boolean) => {
    if (busy) return
    setBusy(true)
    try {
      await setOsAutoUpdate(id, on, ticket ?? 0)
      toast('Auto install auto updates changed.', 'Success')
      await queryClient.invalidateQueries({ queryKey: ['device', id, 'os-updates'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="device-softsee-os">
      <style>{OS_CSS}</style>
      <div className="os-head">
        <div className="os-title">Operating system</div>
        <div className="sum-tiles">
          {page.data.tiles.map((tile) => (
            <OsTileBox key={tile.label} tile={tile} />
          ))}
        </div>
        <div className="os-facts">
          {page.data.facts.map((fact) => (
            <div className="os-fact" key={fact.key}>
              <span className="os-key">{fact.key}</span>
              <span className="os-value" dangerouslySetInnerHTML={{ __html: fact.value }} />
            </div>
          ))}
        </div>
      </div>

      <div className="row ou-panes">
        <div className="col-md-7" onClick={onMissingClick}>
          <div className="ou-pane-title">
            To install{openCount > 0 ? ` · ${openCount}` : ''}
          </div>
          {openCount > 0 ? (
            <PatchTable
              id={MISSING}
              heads={linux ? LINUX_HEADS : WINDOWS_HEADS}
              extra={linux ? ['', 'check'] : ['', 'check']}
              rows={missing.data?.data ?? []}
              total={missing.data?.recordsFiltered ?? 0}
              fetching={missing.isFetching}
              error={missing.isError ? (missing.error as Error).message : null}
              search={missingSearch}
              start={missingStart}
              length={missingLength}
              onSearch={(q) => {
                setMissingSearch(q)
                setMissingStart(0)
              }}
              onStart={setMissingStart}
              onLength={(n) => {
                setMissingLength(n)
                setMissingStart(0)
              }}
            />
          ) : (
            <div className="alert alert-success" style={{ margin: 0 }}>
              <i className="fas fa-check-circle"></i> This system is up to date.
            </div>
          )}
        </div>
        <div className="col-md-5">
          <div className="ou-pane-title">Recently installed</div>
          <PatchTable
            id={INSTALLED}
            heads={WINDOWS_HEADS}
            extra={['check']}
            rows={installed.data?.data ?? []}
            total={installed.data?.recordsFiltered ?? 0}
            fetching={installed.isFetching}
            error={installed.isError ? (installed.error as Error).message : null}
            search={installedSearch}
            start={installedStart}
            length={installedLength}
            onSearch={(q) => {
              setInstalledSearch(q)
              setInstalledStart(0)
            }}
            onStart={setInstalledStart}
            onLength={(n) => {
              setInstalledLength(n)
              setInstalledStart(0)
            }}
          />
        </div>
      </div>

      <div className="ou-actions">
        {missingCount > 0 && (
          <div style={{ display: 'inline-block' }}>
            <button type="button" className="btn btn-sm btn-primary" id="installAllOsUpdates" disabled={busy} onClick={() => void installAll()}>
              <i className="fa fa-check"></i> Install All Updates
            </button>
          </div>
        )}
        <div style={{ display: 'inline-block', marginLeft: 20 }}>
          {/*
            `className="switchery"` era CARLIGUL pluginului Switchery, si in fisa
            asta chiar face rau: `app.css` 12568 ii da 50x30, chenar si colturi
            rotunde, iar pluginul care ar fi pus rotita inauntru nu ruleaza in
            v4. Masurat in Chromium: o pastila alba goala de 50x30 in locul unei
            bife de 15x15 - nici pornita, nici oprita, nimic de citit. Clasa
            pleaca; bifa se vede.

            Bifa e `SettingsCheck`, in desenul nou, cu eticheta in acelasi
            `<label>`. NU trece prin `CheckField` (`ui/FormGrid`): acela sta pe
            `col-md-6`, iar randul asta sta in bara de butoane, langa "Install
            All Updates", nu intr-o grila de formular. Bifa trimite singura, ca
            `.switchery` on change.
          */}
          <form className="autoUpdateOs" onSubmit={(e) => e.preventDefault()}>
            <SettingsCheck
              id="autoUpdateOs"
              name="autoUpdate"
              value="on"
              checked={page.data.auto_update_on}
              disabled={busy}
              onChange={(on) => void toggleAutoUpdate(on)}
              label="Auto Update"
            />
          </form>
        </div>
      </div>
    </div>
  )
}

function OsTileBox({ tile }: { tile: OsTile }) {
  return (
    <div className="sum-tile">
      <div className={`dashboard-stat ${tile.tone}`}>
        <div className="visual">
          <i className={tile.icon}></i>
        </div>
        <div className="details" title={tile.title !== '' ? tile.title : undefined}>
          <div className="number sum-text">{tile.number !== '' ? tile.number : <>&ndash;</>}</div>
          <div className="desc">{tile.label}</div>
          {tile.note !== '' && <div className="sum-note" dangerouslySetInnerHTML={{ __html: tile.note }} />}
        </div>
        <div className="more">&nbsp;</div>
      </div>
    </div>
  )
}

function PatchTable({
  id,
  heads,
  extra,
  rows,
  total,
  fetching,
  error,
  search,
  start,
  length,
  onSearch,
  onStart,
  onLength,
}: {
  id: string
  heads: string[]
  extra: Array<'' | 'check'>
  rows: OsPatchRow[]
  total: number
  fetching: boolean
  error: string | null
  search: string
  start: number
  length: number
  onSearch(q: string): void
  onStart(n: number): void
  onLength(n: number): void
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)
  const [draft, setDraft] = useState(search)
  const typing = useRef<number | null>(null)
  const colSpan = heads.length + extra.length

  useEffect(() => {
    setDraft(search)
  }, [search])
  useEffect(() => {
    return () => {
      if (typing.current !== null) window.clearTimeout(typing.current)
    }
  }, [])

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${id}_wrapper`}>
      <div className="row">
        <div className="col-sm-6 dataTableF">
          <div id={`${id}_filter`} className="dataTables_filter">
            <label>
              Search:{' '}
              <input
                type="search"
                className="form-control input-sm"
                aria-controls={id}
                value={draft}
                onChange={(e) => {
                  const value = e.target.value
                  setDraft(value)
                  if (typing.current !== null) window.clearTimeout(typing.current)
                  typing.current = window.setTimeout(() => onSearch(value), 400)
                }}
              />
            </label>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${id}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          {/*
           * Lista sta intr-un derulator, ca oriunde in portal unde un tabel
           * intra intr-o cutie mai ingusta decat el: `table-responsive` din
           * `app.css`, adica `overflow-x: auto`.
           *
           * Cele unsprezece coloane nu incap in cele sapte doisprezecimi ale
           * panoului. Latimea lor cea mai mica, masurata, e 750px - numai
           * pernele capetelor si sagetile de sortare fac vreo doua treimi -
           * iar coloana are 577px la o fereastra de 1600px si 440px la una
           * de 1366px. `table.dataTable` are `max-width: none !important`,
           * deci tabelul nu se stramteaza: iesea din coloana lui si se
           * desena PESTE lista din dreapta, 143px la 1600px si 280px la
           * 1366px, pe toata inaltimea ei.
           */}
          <div className="table-responsive">
            <table
              className={`table table-striped table-hover dataTable no-footer ${id === MISSING ? 'missing-patches' : 'installed-patches'}`}
              width="100%"
              id={id}
              role="grid"
            >
              <thead>
                <tr role="row">
                  {heads.map((title, i) => (
                    <th
                      key={title}
                      className={`td-small a-left${i === 0 ? ' sorting_asc' : ' sorting'}`}
                      aria-sort={i === 0 ? 'ascending' : undefined}
                    >
                      {title}
                    </th>
                  ))}
                  {extra.map((kind, i) =>
                    kind === 'check' ? (
                      <th key={`c${i}`} className="td-small a-left" dangerouslySetInnerHTML={{ __html: '<center><input type="checkbox" class="allInputs"/></center>' }} />
                    ) : (
                      <th key={`e${i}`} className="td-small a-center"></th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr className="odd">
                    <td colSpan={colSpan} className="dataTables_empty">
                      Something went wrong: {error}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr className="odd">
                    <td colSpan={colSpan} className="dataTables_empty">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                      {row.cells.map((cell, c) => (
                        <td
                          key={c}
                          className={c === heads.length ? 'td-small a-center' : 'td-small a-left'}
                          dangerouslySetInnerHTML={{ __html: cell }}
                        />
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="row os-foot">
        <div className="col-sm-2 os-foot-length">
          <div className="dataTables_length custom_length_class" id={`${id}_length`}>
            <label>
              Show{' '}
              <select
                name={`${id}_length`}
                aria-controls={id}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright os-foot-info">
          <div className="dataTables_info" id={`${id}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6 os-foot-pages">
          <div className="dataTables_paginate paging_simple_numbers" id={`${id}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${id}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${id}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}

const OS_CSS = `
    .os-head .sum-tiles { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 14px; }
    .os-head .sum-tiles .sum-tile { flex: 1 1 200px; min-width: 0; }
    .os-head .sum-tiles .dashboard-stat { margin-bottom: 0; }
    .os-head .sum-tiles .details { min-width: 0; max-width: calc(100% - 95px); }
    .os-head .sum-tiles .sum-text {
        font-size: 17px;
        line-height: 31px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .os-head .sum-tiles .sum-note {
        margin-top: 4px;
        font-size: 11px;
        line-height: 1.35;
        color: rgba(255, 255, 255, .85);
        word-break: break-word;
    }
    .os-facts {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 10px 28px;
        padding: 14px 16px;
        margin-bottom: 18px;
        background: #f7f8fa;
        border: 1px solid #e6e9ee;
        border-radius: 3px;
    }
    @media (max-width: 991px) {
        .os-facts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }
    .os-facts .os-fact { min-width: 0; }
    .os-facts .os-key {
        display: block;
        font-size: 11px;
        letter-spacing: .04em;
        text-transform: uppercase;
        color: #8a94a6;
    }
    .os-facts .os-value {
        display: block;
        font-size: 13px;
        color: #3a4355;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .os-facts .os-unknown { color: #b3bac6; font-style: italic; }
    .os-head .os-title {
        margin: 0 0 10px;
        font-size: 15px;
        font-weight: 600;
        color: #3a4355;
    }
    .ou-actions {
        margin-top: 18px;
        padding-top: 14px;
        border-top: 1px solid #e3e7ec;
    }
    .ou-pane-title {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: .05em;
        color: #8a94a0;
        margin-bottom: 8px;
        padding-bottom: 6px;
        border-bottom: 1px solid #e3e7ec;
    }
    @media (max-width: 991px) {
        .ou-panes .col-md-5 { margin-top: 18px; }
    }

    /*
     * SUBSOLUL TABELULUI INCAPE, SAU TRECE PE RANDUL URMATOR.
     *
     * Randul de jos are cele trei celule ale portalului - meniul de lungime
     * (\`col-sm-2\`), numarul de intrari (\`col-sm-4 aright\`) si paginarea
     * (\`col-sm-6\`) - exact ca in \`dom\`-ul din \`public/js/*.js\`. Toate trei
     * sunt \`white-space: nowrap\`, deci NU se pot stramta: masurate in
     * Chromium, "Show 10 entries" cere 138px, "Showing 1 to 10 of 134
     * entries" 141px, iar paginarea intre 235 si 496px.
     *
     * In restul portalului randul asta are toata latimea paginii si incap.
     * Aici listele stau doua alaturi, in sapte si cinci doisprezecimi, si
     * \`col-sm-2\` din panoul din dreapta iese 78px la o fereastra de 1366px.
     * Meniul, lat de 138px, iesea din celula lui peste cea de alaturi, si
     * cuvintele "Showing 1 to 10 of 134 entries" se desenau PESTE el:
     * suprapunere masurata de 43px la 1366px si de numai 5px la 1600px -
     * adica gata sa reapara la primul numar mai lung.
     *
     * Randul devine o linie care se INDOAIE: celulele isi pastreaza ordinea
     * si alinierea, dar cand nu mai incap toate trei, paginarea coboara pe
     * randul de sub ele in loc sa le calce. Unde e loc - panoul lat, ferestrele
     * mari - nu se schimba nimic.
     */
    .device-softsee-os .os-foot {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        row-gap: 6px;
    }
    .device-softsee-os .os-foot > div {
        float: none;
        width: auto;
        min-width: 0;
    }
    .device-softsee-os .os-foot > .os-foot-length { flex: 0 0 auto; }
    .device-softsee-os .os-foot > .os-foot-info { flex: 1 1 auto; }
    .device-softsee-os .os-foot > .os-foot-pages { flex: 1 1 auto; }
`
